import { Layout } from "antd";
import { type BasicProps } from "antd/lib/layout/layout";
import {
    memo,
    PureComponent,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from "react";
import { White } from "styles/buildertrendTheme/Colors";

import { cn } from "@btnet/utilities-core";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";
import { routesWebforms } from "utilities/routesWebforms";

import { BTListLayout } from "commonComponents/btWrappers/BTListLayout/BTListLayout";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { Hotkey } from "commonComponents/utilities/Hotkey/Hotkey";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";
import { StickyFooterClassname } from "commonComponents/utilities/ListActionBar/ListActionBar";

import "./BTListLayoutBDS.less";

export type RenderListActions = (header?: React.ReactNode) => React.ReactNode;

interface IBTListLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    title: string | ReactNode;
    className?: string;
    jobName?: string;
    listActions?: ReactNode;
    contentHeader?: ReactNode;
    renderWithActions?: (renderListActions: RenderListActions) => React.ReactNode;
    "data-testid"?: string;
    optionalHeader?: React.ReactNode;
    wrapInFocusProvider?: boolean;
    children?: ReactNode;

    /**
     * Show the default list page header and list title. Avoid setting this to false using this except
     * for list pages that are very unique and handle this from within the list component itself
     *
     * @default true
     */
    showHeader?: boolean;

    /**
     * This property is TEMPORARY and is NOT intended to be used by developers to fix bugs.
     * You MUST make your pages repsonsive. This property was added to assist with the migration, do not use.
     * @deprecate You are expected to make your page responsive. Yes it's ALWAYS possible
     */
    dangerousDoNotUseIExpectAPassbackPageMinWidth?: number;
    /**
     * The flag name to determine if the list should be displayed using the BDS components or the legacy components.
     */
    flagName?: string;
}

export const BTListLayoutBDS: React.FunctionComponent<IBTListLayoutProps> = (props) => {
    const {
        title,
        children,
        className,
        jobName,
        listActions,
        "data-testid": testid,
        renderWithActions,
        optionalHeader,
        dangerousDoNotUseIExpectAPassbackPageMinWidth,
        style,
        wrapInFocusProvider,
        showHeader = true,
        contentHeader,
        flagName,
        ...otherProps
    } = props;

    const builderInfo = useContext(BuilderInfoContext);

    const listLayoutClass = cn("BTListLayoutBDS react", className);

    // This callback will be passed down to be rendered inside the GridContainer's toolbar render function
    const getHeader: RenderListActions = (header) => {
        if (!showHeader) {
            return null;
        }
        if (header) {
            return header;
        } else {
            return contentHeader;
        }
    };
    const handleContentResize = (entry: ResizeObserverEntry | undefined) => {
        if (entry?.target) {
            window.dispatchEvent(new Event("BTLayoutResize"));
        }
    };
    const header = renderWithActions ? renderWithActions(getHeader) : getHeader();

    const [, onDivRefChange] = useResizeObserver<HTMLDivElement>(handleContentResize);

    const content = (
        <div ref={onDivRefChange}>
            {/* eslint-disable-next-line react/forbid-elements*/}
            <Layout
                data-testid={testid}
                className={listLayoutClass}
                style={{
                    ...style,
                    minWidth: dangerousDoNotUseIExpectAPassbackPageMinWidth,
                }}
                {...otherProps}
            >
                {header}
                {children}
            </Layout>
        </div>
    );

    let returnContent = content;
    if (wrapInFocusProvider) {
        returnContent = (
            <FocusProvider shareFocusWithParent>
                <Hotkey
                    {...HotkeyCommands["close"]}
                    // eslint-disable-next-line deprecate/member-expression
                    onCommand={() => routesWebforms.closeModalAndReload()}
                    triggerBehavior="down"
                />
                {content}
            </FocusProvider>
        );
    }
    if (flagName && builderInfo?.flags?.[flagName as keyof typeof builderInfo.flags] !== true) {
        const { flagName: _flagName, contentHeader: _contentHeader, ...legacyProps } = props;
        return <BTListLayout {...legacyProps}>{children}</BTListLayout>;
    }
    return returnContent;
};

interface IListActionsProps extends BasicProps {
    isSticky?: boolean;
    checkedActions?: boolean;
}

export const ListActions: React.FunctionComponent<IListActionsProps> = memo<IListActionsProps>(
    ({ children, className, isSticky = false, checkedActions = false }) => {
        return (
            <Layout.Header
                className={cn("ListActions", className, {
                    StickyLayoutHeader: isSticky,
                    CheckedActions: checkedActions,
                })}
                style={{ height: "auto" }}
            >
                {children}
            </Layout.Header>
        );
    },
);

export interface IListSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    style?: React.CSSProperties;
}

export class ListSection extends PureComponent<IListSectionProps> {
    render() {
        const { className, children, ...otherProps } = this.props;
        const listSectionClass = cn("ListSection", "react", className);
        return (
            <div className={listSectionClass} {...otherProps}>
                {children}
            </div>
        );
    }
}

interface IBTListLayoutWhitespaceProps {
    className?: string;
    children?: ReactNode;
}

export const BTListLayoutWhitespace: React.FunctionComponent<IBTListLayoutWhitespaceProps> = (
    props,
) => {
    const { className } = props;
    const combinedClasses = cn(className, "BTListLayoutWhitespace", "flex justify-content-center");
    const container = useRef<HTMLDivElement>(null);

    const fixDivHeight = useCallback(() => {
        if (container.current) {
            // calculate how much space the whitespace should take up,
            // the area between content above and the footer below (on pages with sticky footer)
            const currentTop = container.current.getBoundingClientRect().top;
            const footerHeight = container.current.parentElement!.querySelector(
                "." + StickyFooterClassname,
            )?.clientHeight;
            const offset = footerHeight ? currentTop + footerHeight : currentTop;

            // Take an additional 20px off to account for horizontal scrollbar heights
            const calculatedMinHeight = window.innerHeight - offset - 20;

            const minHeightValue = Math.max(0, calculatedMinHeight) + "px";
            if (container.current.style.minHeight !== minHeightValue) {
                container.current.style.minHeight = minHeightValue;
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener("BTLayoutResize", fixDivHeight);
        return () => {
            window.removeEventListener("BTLayoutResize", fixDivHeight);
        };
    }, [fixDivHeight]);

    return (
        <div ref={container} className={combinedClasses} style={{ background: White }}>
            {props.children}
        </div>
    );
};
